/**
 * Cinematic celebrations used by GamePlay.jsx — pure functions, no React.
 *
 *  • fireSupertConfetti(ref)  → ~1s burst around the SUPERT bar (mid-cycle)
 *  • fireVictoryConfetti()    → ~4s grand-finale (final SUPERT cycle won)
 */
import confetti from "canvas-confetti";

/**
 * Fire 4 quick confetti bursts centered on the SUPERT bar DOM element.
 * Pass a React ref pointing to the bar's wrapper div.
 */
export function fireSupertConfetti(supertBarRef) {
  try {
    const el = supertBarRef?.current;
    if (!el || typeof window === "undefined") return;
    const r = el.getBoundingClientRect();
    const x = (r.left + r.width / 2) / window.innerWidth;
    const y = (r.top + r.height / 2) / window.innerHeight;
    const colors = [
      "#FFD700", "#FF6B6B", "#4ECDC4", "#A78BFA",
      "#34D399", "#F472B6", "#60A5FA", "#FBBF24",
    ];
    const fire = (opts) =>
      confetti({
        spread: 70, startVelocity: 45, ticks: 200, zIndex: 50, colors,
        origin: { x, y }, ...opts,
      });
    fire({ particleCount: 60, spread: 100 });
    setTimeout(() => fire({ particleCount: 45, angle: 60, spread: 55, origin: { x: x - 0.05, y } }), 200);
    setTimeout(() => fire({ particleCount: 45, angle: 120, spread: 55, origin: { x: x + 0.05, y } }), 400);
    setTimeout(() => fire({ particleCount: 50, spread: 120, startVelocity: 35 }), 900);
  } catch (e) {
    console.error("[confetti]", e);
  }
}

/**
 * 4-second grand-finale burst for the FINAL victory (last SUPERT cycle).
 * Combines a big central burst + two side cannons + extra mid bursts.
 */
export function fireVictoryConfetti() {
  try {
    if (typeof window === "undefined") return;
    const gold = ["#FFD700", "#FFC107", "#FFEB3B", "#FFF59D", "#FBBF24", "#F59E0B"];
    const multi = [
      "#FFD700", "#FF6B6B", "#4ECDC4", "#A78BFA",
      "#34D399", "#F472B6", "#60A5FA", "#FBBF24",
    ];
    confetti({
      particleCount: 180, spread: 160, startVelocity: 55, ticks: 260,
      zIndex: 60, origin: { x: 0.5, y: 0.45 }, colors: multi,
    });
    const duration = 4000;
    const animationEnd = Date.now() + duration;
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) { clearInterval(interval); return; }
      const particleCount = 40 * (timeLeft / duration);
      confetti({ particleCount, angle: 60, spread: 70, startVelocity: 55, ticks: 240, zIndex: 60, origin: { x: 0, y: 0.85 }, colors: gold });
      confetti({ particleCount, angle: 120, spread: 70, startVelocity: 55, ticks: 240, zIndex: 60, origin: { x: 1, y: 0.85 }, colors: gold });
    }, 280);
    setTimeout(() => confetti({ particleCount: 120, spread: 140, startVelocity: 45, ticks: 220, zIndex: 60, origin: { x: 0.25, y: 0.55 }, colors: multi }), 700);
    setTimeout(() => confetti({ particleCount: 120, spread: 140, startVelocity: 45, ticks: 220, zIndex: 60, origin: { x: 0.75, y: 0.55 }, colors: multi }), 1400);
  } catch (e) {
    console.error("[victory confetti]", e);
  }
}

/**
 * 8-second EPIC finalist celebration — bigger, louder, longer than the
 * regular victory burst. Fires golden cannons from all four corners, a
 * central golden eruption, then trickling golden snowfall.
 */
export function fireFinalistConfetti() {
  try {
    if (typeof window === "undefined") return;
    const gold = ["#FFD700", "#FFC107", "#FFEB3B", "#FFF59D", "#FBBF24", "#F59E0B", "#FBBF24"];
    const purple = ["#7C3AED", "#A78BFA", "#C4B5FD", "#FFD700", "#FBBF24"];

    // 1) Massive central burst
    confetti({
      particleCount: 300, spread: 180, startVelocity: 65, ticks: 320,
      zIndex: 80, origin: { x: 0.5, y: 0.4 }, colors: purple,
    });

    // 2) Side cannons — 7 seconds of continuous golden rain
    const duration = 7000;
    const animationEnd = Date.now() + duration;
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) { clearInterval(interval); return; }
      const pc = 55 * (timeLeft / duration);
      // Bottom-left cannon
      confetti({ particleCount: pc, angle: 60, spread: 80, startVelocity: 65, ticks: 280, zIndex: 80, origin: { x: 0.02, y: 0.9 }, colors: gold });
      // Bottom-right cannon
      confetti({ particleCount: pc, angle: 120, spread: 80, startVelocity: 65, ticks: 280, zIndex: 80, origin: { x: 0.98, y: 0.9 }, colors: gold });
      // Top-left cannon (downward rain)
      confetti({ particleCount: pc * 0.6, angle: -60, spread: 50, startVelocity: 25, ticks: 200, zIndex: 80, origin: { x: 0.05, y: 0.05 }, colors: gold, gravity: 1.6 });
      // Top-right cannon (downward rain)
      confetti({ particleCount: pc * 0.6, angle: -120, spread: 50, startVelocity: 25, ticks: 200, zIndex: 80, origin: { x: 0.95, y: 0.05 }, colors: gold, gravity: 1.6 });
    }, 250);

    // 3) Extra mid bursts to keep the energy
    setTimeout(() => confetti({ particleCount: 200, spread: 160, startVelocity: 55, ticks: 260, zIndex: 80, origin: { x: 0.3, y: 0.55 }, colors: purple }), 900);
    setTimeout(() => confetti({ particleCount: 200, spread: 160, startVelocity: 55, ticks: 260, zIndex: 80, origin: { x: 0.7, y: 0.55 }, colors: purple }), 1800);
    setTimeout(() => confetti({ particleCount: 250, spread: 180, startVelocity: 60, ticks: 300, zIndex: 80, origin: { x: 0.5, y: 0.5 }, colors: gold }), 3000);
    setTimeout(() => confetti({ particleCount: 180, spread: 180, startVelocity: 50, ticks: 280, zIndex: 80, origin: { x: 0.5, y: 0.5 }, colors: purple }), 4500);
  } catch (e) {
    console.error("[finalist confetti]", e);
  }
}

/**
 * Play a celebratory fanfare for finalists. Looks up the URL of a sound
 * configured for the game (victory_sound) but plays at higher volume and
 * stacks a short crowd-cheer-style ascending arpeggio synthesized via the
 * Web Audio API so we always have a "wow" moment even when no asset is set.
 */
export function playFinalistFanfare(audioCtxFactory) {
  try {
    if (typeof window === "undefined") return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = (audioCtxFactory && audioCtxFactory()) || new AudioCtx();
    const now = ctx.currentTime;
    // Major triad arpeggio C5-E5-G5-C6 then a sustained C6 chord.
    const notes = [
      { f: 523.25, t: 0.0, d: 0.18, g: 0.18 },  // C5
      { f: 659.25, t: 0.13, d: 0.18, g: 0.18 }, // E5
      { f: 783.99, t: 0.26, d: 0.18, g: 0.18 }, // G5
      { f: 1046.5, t: 0.39, d: 0.40, g: 0.22 }, // C6 — high & sustained
      { f: 1318.5, t: 0.39, d: 0.40, g: 0.14 }, // E6 stacked
      { f: 1567.98, t: 0.55, d: 0.55, g: 0.18 },// G6 climax
    ];
    notes.forEach((n) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(n.f, now + n.t);
      gain.gain.setValueAtTime(0.0001, now + n.t);
      gain.gain.exponentialRampToValueAtTime(n.g, now + n.t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + n.t + n.d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + n.t);
      osc.stop(now + n.t + n.d + 0.05);
    });
    // A soft "crowd cheer" using filtered noise.
    const noiseLen = ctx.sampleRate * 1.5;
    const noiseBuffer = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < noiseLen; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / noiseLen);
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 1800;
    filter.Q.value = 0.7;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0001, now);
    ng.gain.exponentialRampToValueAtTime(0.12, now + 0.15);
    ng.gain.exponentialRampToValueAtTime(0.0001, now + 1.4);
    noise.connect(filter).connect(ng).connect(ctx.destination);
    noise.start(now);
    noise.stop(now + 1.5);
  } catch (e) {
    console.error("[finalist fanfare]", e);
  }
}
